import { FC, useCallback, useEffect, useMemo, useRef } from "react"
import { List, ListItemButton, useTheme } from "@mui/material"
import { Options, Splide, SplideSlide } from "@splidejs/react-splide"
import "@splidejs/react-splide/css"
import { CorrelationFloorOption } from "utils/feature"
import { first } from "lodash"
import { Id } from "@turf/helpers"
import clsx from "clsx"

export interface CorrelationSelectorSplideProps {
  correlationOptions: CorrelationFloorOption[]
  onChange: (id: Id) => void
  selectedFeatureId: Id
  options: Options
  syncWithActiveSlide?: boolean
  className?: string
}

export const CorrelationSelectorSplide: FC<CorrelationSelectorSplideProps> = ({
  correlationOptions,
  onChange,
  selectedFeatureId,
  options = {},
  syncWithActiveSlide = true,
  className,
}) => {
  const splideRef = useRef(null)
  const theme = useTheme()
  const { listItemButton, root } =
    theme.components[`CorrelationSelectorSplide`]?.styleOverrides || {}

  const { carouselOptions = {} } =
    theme.components[`CorrelationSelectorSplide`]?.defaultProps || {}

  const handleChange = useCallback(
    (id) => () => {
      onChange(id)
    },
    [onChange]
  )
  const selectedCorrelationIndex = useMemo(
    () =>
      correlationOptions.findIndex(
        (correlationOption) => correlationOption.id === selectedFeatureId
      ),
    [correlationOptions, selectedFeatureId]
  )

  /**
   * NOTE: for prevent onActive trigger when levels is changed
   * This is because the splide is not re-rendering when the levels change.
   * This is a workaround to force the splide to re-render when the levels change.
   */
  const splideKey = useMemo(
    () => `${first(correlationOptions)?.id}`,
    [correlationOptions]
  )

  const handleDragging = useCallback((splide) => {
    const slides = splide.root.querySelectorAll(" .splide__slide")
    slides.forEach((slide) => {
      slide.classList.add("is-dragging")
    })
  }, [])

  const handleDragged = useCallback((splide) => {
    const slides = splide.root.querySelectorAll(" .splide__slide")
    slides.forEach((slide) => {
      slide.classList.remove("is-dragging")
    })
  }, [])

  const handleChangeActiveSlide = useCallback(
    (_splide, slideData) => {
      if (!syncWithActiveSlide) return
      onChange(slideData.slide.dataset.id)
    },
    [onChange, syncWithActiveSlide]
  )

  const handleOnReady = useCallback(
    (splide) => {
      /**
       * Workaround to center the active slide after page refresh.
       */
      setTimeout(() => {
        splide.go(selectedCorrelationIndex + 1)
        splide.go(selectedCorrelationIndex)
      }, 250)
    },
    [selectedCorrelationIndex]
  )

  const handleClickSlide = useCallback(
    (_splide, slideData) => {
      onChange(slideData.slide.dataset.id)
    },
    [onChange]
  )

  const handleOrdinalChange = useCallback((index) => {
    const splide = splideRef.current?.splide
    if (splide) {
      splide.go(index)
    }
  }, [])

  useEffect(() => {
    handleOrdinalChange(selectedCorrelationIndex)
  }, [selectedCorrelationIndex, handleOrdinalChange])

  if (correlationOptions.length <= 0) return null

  return (
    <List
      sx={root}
      className={clsx("CorrelationSelectorSplide-root", className)}
    >
      <Splide
        key={splideKey}
        options={{
          type: "slide",
          perPage: 3,
          pagination: false,
          arrows: false,
          focus: "center",
          drag: "free",
          snap: true,
          trimSpace: true,
          direction: "ttb",
          height: "204px",
          gap: "24px",
          updateOnMove: true,
          start: selectedCorrelationIndex >= 0 ? selectedCorrelationIndex : 0,
          ...carouselOptions,
          ...options,
        }}
        aria-label="Slide"
        onDrag={handleDragging}
        onActive={handleChangeActiveSlide}
        onDragged={handleDragged}
        onClick={handleClickSlide}
        onReady={handleOnReady}
        ref={splideRef}
      >
        {correlationOptions.map((correlationOption) => {
          const id = correlationOption.id
          return (
            <SplideSlide
              key={`correlation-selector-${correlationOption.id}`}
              data-id={id}
            >
              <ListItemButton
                className="CorrelationSelectorSplide-listItemButton"
                sx={listItemButton}
                selected={id === selectedFeatureId}
                onClick={handleChange(id)}
              >
                {correlationOption.levelName}
              </ListItemButton>
            </SplideSlide>
          )
        })}
      </Splide>
    </List>
  )
}
